import React, { useRef } from 'react';
import { Button, Input, Typography, Collapse, Select } from 'antd';
import Form from 'components/Form';
import PhoneNumber from 'components/PhoneNumber';
import { getPhoneNumberValidator } from 'utils';
import { USA_STATES_DROPDOWN_OPTIONS } from 'constants';
import { CardFooter } from 'components/Card/Card';

const { Text } = Typography;
const { Panel } = Collapse;

const ProviderAdvancedSearchForm = ({ state, form, onSubmit }) => {
  const activeKeysRef = useRef([]);
  const panelRefs = useRef({});

  // Transform USA_STATES_DROPDOWN_OPTIONS to match the `options` format
  const options = USA_STATES_DROPDOWN_OPTIONS.map((state) => ({
    value: state.code,
    label: state.name,
  }));

  const handleCollapseChange = (keys) => {
    const next = Array.isArray(keys) ? keys : [keys];
    const newlyOpened = next.find((k) => !activeKeysRef.current.includes(k));
    activeKeysRef.current = next;
    if (newlyOpened) {
      requestAnimationFrame(() => {
        panelRefs.current[newlyOpened]?.querySelector('input')?.focus();
      });
    }
  };

  const resetSearch = () => {
    form.resetFields();
    onSubmit({});
  };

  return (
    <Form form={form} layout="vertical" onFinish={onSubmit} data-testid="advanced-search-form">
      <div className="p-7">
        <span>Search by one or all criteria</span>

        <Collapse bordered={true} defaultActiveKey={[]} className="mt-4" onChange={handleCollapseChange}>
          <Panel header="Address" key="address">
            <div
              ref={(el) => {
                panelRefs.current.address = el;
              }}
            >
              <Form.Item label="Address line 1" name={['location', 'address', 'street']}>
                <Input placeholder="" />
              </Form.Item>
              <Form.Item label="Address line 2" name={['location', 'address', 'street2']}>
                <Input placeholder="" />
              </Form.Item>
              <Form.Item label="City" name={['location', 'address', 'city']}>
                <Input placeholder="" />
              </Form.Item>
              <Form.Item label="State">
                <Text type="secondary">Select one or more.</Text>
                <Form.Item name={['location', 'states']} noStyle>
                  <Select
                    mode="multiple"
                    placeholder="Select"
                    options={options}
                    maxTagCount={1}
                    allowClear
                    size="small"
                    labelRender={(props) => {
                      // Find the state object from the selected value
                      const state = USA_STATES_DROPDOWN_OPTIONS.find((state) => state.code === props.value);
                      // Render the abbreviated state code (e.g., 'CA')
                      return <span>{state ? state.code : props.value}</span>;
                    }}
                  />
                </Form.Item>
              </Form.Item>
              <Form.Item label="Zip code" name={['location', 'address', 'postalCode']}>
                <Input placeholder="" />
              </Form.Item>
            </div>
          </Panel>

          <Panel header="Location ID" key="facilityId">
            <div
              ref={(el) => {
                panelRefs.current.facilityId = el;
              }}
            >
              <Form.Item label="Location ID" name="careFacilityId">
                <Input placeholder="" />
              </Form.Item>
            </div>
          </Panel>

          <Panel header="Phone number" key="locationPhone">
            <div
              ref={(el) => {
                panelRefs.current.locationPhone = el;
              }}
            >
              <Form.Item
                label="Phone number"
                name="locationPhone"
                rules={[getPhoneNumberValidator('Invalid phone number!')]}
              >
                <PhoneNumber />
              </Form.Item>
            </div>
          </Panel>

          <Panel header="Email address" key="email">
            <div
              ref={(el) => {
                panelRefs.current.email = el;
              }}
            >
              <Form.Item
                label="Email address"
                name="email"
                rules={[{ type: 'email', message: 'Invalid email address!' }]}
              >
                <Input placeholder="provider@example.com" />
              </Form.Item>
            </div>
          </Panel>

          <Panel header="Provider name" key="providerName">
            <div
              ref={(el) => {
                panelRefs.current.providerName = el;
              }}
            >
              <Form.Item label="Doing business as (DBA)" name="doingBusinessAs">
                <Input placeholder="" />
              </Form.Item>
              <Form.Item label="Legal business name" name="businessLegalName">
                <Input placeholder="" />
              </Form.Item>
            </div>
          </Panel>

          <Panel header="Tax ID (EIN)" key="taxId">
            <div
              ref={(el) => {
                panelRefs.current.taxId = el;
              }}
            >
              <Form.Item label="Tax ID (EIN)" name="taxId" extra="With or without dash">
                <Input placeholder="12-3456789" />
              </Form.Item>
            </div>
          </Panel>

          <Panel header="License number" key="licenseNumber">
            <div
              ref={(el) => {
                panelRefs.current.licenseNumber = el;
              }}
            >
              <Form.Item label="License number" name="licenseNumber">
                <Input placeholder="VA-FCC-2024-12345" />
              </Form.Item>
            </div>
          </Panel>
        </Collapse>
      </div>

      <CardFooter className="actions p-7">
        <Button type="default" onClick={resetSearch} data-testid="reset-button">
          Reset search
        </Button>

        <Button
          type="primary"
          htmlType="submit"
          loading={state.loading}
          disabled={state.disabled}
          className="ml-2"
          data-testid="search-button"
        >
          Search
        </Button>
      </CardFooter>
    </Form>
  );
};

export default ProviderAdvancedSearchForm;
